// ============================================================================
// CompensationSection (L16 §B.5, F-845..F-848) — remuneration, LECTURE SEULE
// ============================================================================
// SENSIBLE : rendu uniquement si le SERVEUR a accorde l'onglet (soi-meme OU
// canReadCompensation) — il ne renvoie meme pas `compensation` sinon.
//
// ⚠ GARDE-FOU M-816 : ce composant ne peut PAS rendre off_grid_reason_private ni
// role_fit_note — le serveur ne les SELECT pas (repositories/compensation.ts). La
// seule raison qui sort cote employe est `offGridReasonPublic` (F-848).
//
// ─── PERIMETRE V1 (arbitrage David, STOP-question du MASTER) ─────────────────
// Le PoC calcule ici boni (M-817), avantages employeur (M-820), surcout d'heures
// speciales et primes (M-818/M-819), derniere evaluation, et rend une frise
// SalaryProgressionPanel (F-849) — le tout sur 9 lectures et un composant HORS du
// manifeste L16. Ces dimensions arrivent au FRAG 7 avec leur couche data.
// Ici : Salaire (grille/taux reel, M-812/M-813/M-814), hors-grille (M-815/M-816),
// et les 2 EmptyStates (F-845/F-846). Les dimensions differees affichent « — »
// avec une note explicite — JAMAIS un 0 qui se lirait comme « aucun boni ».

import { Wallet } from 'lucide-react';
import { EmptyState, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { maxEchelonForNiveau, resolveNiveau } from '../../lib/salary/niveau';
import type { ProfileCompensation } from '../../hooks/useProfile';

const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800';

function StatTile({ label, value, hint, tone }: {
  label: string; value: string; hint: string; tone?: 'brand' | 'success';
}) {
  const valueTone =
    tone === 'brand' ? 'text-brand-600 dark:text-brand-400'
    : tone === 'success' ? 'text-emerald-700 dark:text-emerald-400'
    : 'text-slate-800 dark:text-slate-100';
  return (
    <div className={CARD}>
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-head font-bold tabular-nums ${valueTone}`}>{value}</p>
      <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">{hint}</p>
    </div>
  );
}

export interface CompensationSectionProps {
  compensation: ProfileCompensation | null;
}

export function CompensationSection({ compensation }: CompensationSectionProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('en') ? 'en-CA' : 'fr-CA';
  const currentYear = new Date().getFullYear();

  const money = (n: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);
  const hourly = (n: number) =>
    t('gest:profile.compensation.hourly', {
      rate: new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n),
    });

  // F-845 : aucune fiche, ou fiche non positionnee sur une classe.
  if (!compensation || !compensation.classId) {
    return (
      <section className="flex flex-col gap-3">
        <EmptyState
          icon={<Wallet size={48} aria-hidden="true" />}
          title={t('gest:profile.compensation.emptyTitle')}
          description={t('gest:profile.compensation.emptyDescription')}
        />
      </section>
    );
  }

  // F-846 : la classe n'existe plus dans la grille de l'annee courante (M-812).
  const cls = compensation.gridClass;
  if (!cls) {
    return (
      <section className="flex flex-col gap-3">
        <EmptyState
          icon={<Wallet size={48} aria-hidden="true" />}
          title={t('gest:profile.compensation.classGoneTitle')}
          description={t('gest:profile.compensation.classGoneDescription')}
        />
      </section>
    );
  }

  // M-814 : niveau REEL persiste (jamais deduit de l'echelon — les plages se
  // chevauchent) ; le plafond du niveau borne l'avancement. Les libs niveau.ts
  // attendent la forme PoC (`echelon_max`) — adaptation locale du DTO camelCase.
  const niveaux = cls.niveaux.map((n) => ({
    level: n.level,
    label: n.label,
    echelon_max: n.echelonMax,
    // `description` du niveau : non lue par la vue profil (elle ne la rend pas) —
    // null explicite plutot que de la SELECT « au cas ou ».
    description: null,
  }));
  // Les libs niveau.ts attendent la forme PoC complete. `maxEchelonForNiveau` ne
  // lit que `echelons.length` et `niveaux` : les champs de comparaison annuelle
  // (rate_2025 / pct_from_prev) ne sont donc ni SELECT ni fabriques — null
  // explicite, jamais une valeur inventee qui pourrait etre affichee un jour.
  const clsForNiveau = {
    echelons: cls.echelons.map((e) => ({
      echelon: e.echelon, rate: e.rate, rate_2025: null, pct_from_prev: null,
    })),
    niveaux,
  };
  const niveau = resolveNiveau(niveaux, compensation.currentNiveau);
  const cap = maxEchelonForNiveau(clsForNiveau, compensation.currentNiveau);
  const next = compensation.currentEchelon + 1 <= cap
    ? cls.echelons.find((e) => e.echelon === compensation.currentEchelon + 1)
    : undefined;
  const atCap = niveau != null && compensation.currentEchelon >= cap;

  // Taux effectif : le taux REEL du record prime sur le taux de grille (parite
  // PoC `timeline.currentHourly` : reel date s'il existe, sinon grille).
  const gridRate = cls.echelons.find((e) => e.echelon === compensation.currentEchelon)?.rate ?? null;
  const effectiveHourly = compensation.currentHourlyRate ?? gridRate;

  // M-815 : hors grille SANS taux reel saisi -> AUCUN montant (ce serait faux).
  const offGridUnpriced = compensation.offGrid && compensation.currentHourlyRate === null;
  // M-813 : heures reelles si renseignees, sinon celles de la classe (deja resolu
  // serveur) -> prorata temps partiel du salaire grille.
  const annual = effectiveHourly !== null && !offGridUnpriced
    ? effectiveHourly * compensation.weeklyHours * cls.weeksPerYear
    : null;

  const salaryHint = offGridUnpriced
    ? t('gest:profile.compensation.offGridUnpriced')
    : compensation.offGrid
      ? (effectiveHourly !== null ? hourly(effectiveHourly) : '—')
      : effectiveHourly !== null
        ? t('gest:profile.compensation.gridHint', {
            rate: hourly(effectiveHourly), echelon: compensation.currentEchelon, max: cls.echelons.length,
          })
        : t('gest:profile.compensation.echelonHint', {
            echelon: compensation.currentEchelon, max: cls.echelons.length,
          });

  return (
    <section className="flex flex-col gap-4">
      {/* F-847 : les 4 indicateurs. Boni/Avantages/Total = differes FRAG 7 -> « — »
          + note explicite (jamais un 0 qui se lirait « aucun boni »). */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          tone="brand"
          label={t('gest:profile.compensation.salary', { year: currentYear })}
          value={annual !== null ? money(annual) : '—'}
          hint={salaryHint}
        />
        <StatTile
          label={t('gest:profile.compensation.bonus', { year: currentYear })}
          value="—"
          hint={t('gest:profile.compensation.deferred')}
        />
        <StatTile
          label={t('gest:profile.compensation.benefits')}
          value="—"
          hint={t('gest:profile.compensation.deferred')}
        />
        <StatTile
          tone="success"
          label={t('gest:profile.compensation.total')}
          value="—"
          hint={t('gest:profile.compensation.deferred')}
        />
      </div>

      {/* F-848 : encadre hors grille — raison PUBLIQUE seulement (M-816). Le
          fallback generique reproduit celui du PoC quand la raison est absente. */}
      {compensation.offGrid ? (
        <div role="note" className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900 dark:bg-blue-950/40">
          <p className="text-field font-semibold text-blue-900 dark:text-blue-200">
            {t('gest:profile.compensation.offGridTitle')}
          </p>
          <p className="mt-1 text-field text-blue-800 dark:text-blue-300">
            {compensation.offGridReasonPublic ?? t('gest:profile.compensation.offGridFallback')}
          </p>
        </div>
      ) : (
        // F-849 : la frise SalaryProgressionPanel est HORS lot L16 et depend de la
        // couche data FRAG 7 -> degradation propre : classe/niveau/plafond servis
        // en clair plutot qu'un graphique vide.
        <div className={`${CARD} flex flex-wrap items-center justify-between gap-3`}>
          <div className="min-w-0">
            <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{cls.label}</p>
            <p className="mt-0.5 text-meta text-slate-500 dark:text-slate-400">
              {niveau
                ? t('gest:profile.compensation.niveauLabel', { level: niveau.level, label: niveau.label })
                : t('gest:profile.compensation.niveauUndefined')}
              {' · '}
              {t('gest:profile.compensation.gridYear', { year: compensation.gridYear })}
            </p>
          </div>
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:profile.compensation.progressionDeferred')}
          </p>
        </div>
      )}

      {/* F-849 (hints) : prochain echelon / plafond atteint — sur grille seulement.
          Le bloc « derniere evaluation » depend de gest.Evaluations (FRAG 7). */}
      {!compensation.offGrid && next ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:profile.compensation.nextEchelon', { echelon: next.echelon, rate: hourly(next.rate) })}
        </p>
      ) : !compensation.offGrid && atCap ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:profile.compensation.atCap', { cap, total: cls.echelons.length })}
        </p>
      ) : null}
    </section>
  );
}
